import { nextTurnstileToken, turnstileHeaders } from '@components/Turnstile/Turnstile';
import type {
  FVTReferralCreateRequest,
  FVTReferralDetails,
  FVTReferralFamilyMemberCreateRequest,
  FVTReferralFamilyMemberDetails,
} from '@customTypes/referrals';
import axios from '@hooks/useAxios';

const FVT_API_ROUTES = {
  authSigninToken: '/patient-portal/auth/signin/token',
  createFVTReferral: '/patient-portal/fvt/referral',
  getFVTReferral: (uuid: string) => `/patient-portal/fvt/referral/${uuid}`,
  createFVTReferralFamilyMember: (uuid: string) => `/patient-portal/fvt/referral/${uuid}/family-member`,
};

// Default axios timeout is 10s; a 25 MB PDF can exceed that on slow uploads.
// Bump just this one call rather than raising the global default.
const FVT_REFERRAL_TIMEOUT_MS = 60_000;

export const requestFVTReferralSignInToken = async (email: string): Promise<void> => {
  const turnstileToken = await nextTurnstileToken();
  await axios.post(FVT_API_ROUTES.authSigninToken, { email }, { headers: turnstileHeaders(turnstileToken) });
};

export const createFVTReferral = async (payload: FVTReferralCreateRequest): Promise<void> => {
  const formData = new FormData();
  formData.append('campaign_code', payload.campaign_code);
  formData.append('email', payload.email);
  if (payload.first_name) formData.append('first_name', payload.first_name);
  if (payload.last_name) formData.append('last_name', payload.last_name);
  if (payload.provider_first_name) formData.append('provider_first_name', payload.provider_first_name);
  if (payload.provider_last_name) formData.append('provider_last_name', payload.provider_last_name);
  if (payload.provider_email) formData.append('provider_email', payload.provider_email);
  formData.append('report_pdf', payload.report_pdf, payload.report_pdf.name);
  formData.append('eligibility', JSON.stringify(payload.eligibility));
  formData.append('clinical_history', JSON.stringify(payload.clinical_history));

  const turnstileToken = await nextTurnstileToken();
  await axios.post(FVT_API_ROUTES.createFVTReferral, formData, {
    timeout: FVT_REFERRAL_TIMEOUT_MS,
    suppressGlobalErrorToast: true,
    headers: turnstileHeaders(turnstileToken),
  });
};

export const getFVTReferral = async (referralUuid: string): Promise<FVTReferralDetails> => {
  const url = FVT_API_ROUTES.getFVTReferral(referralUuid);
  const response = await axios.get(url);
  return response.data;
};

export const createFVTReferralFamilyMember = async (
  referralUuid: string,
  payload: FVTReferralFamilyMemberCreateRequest,
): Promise<FVTReferralFamilyMemberDetails> => {
  const url = FVT_API_ROUTES.createFVTReferralFamilyMember(referralUuid);
  const response = await axios.post(url, payload);
  return response.data;
};
